// Pure logic backing the "Record now" mode of the Send-a-recording card on the
// Staff Alerts page: turn a browser microphone capture into the exact artifacts
// the shipped NxHubBatch pipeline consumes. Kept out of the React component so it
// runs in the repo's ts-jest node env — the same logic-in-helpers convention as
// alertTargets.ts / confirmations.ts / sendRecording.ts.
//
// The flow rides existing endpoints end-to-end (no new backend surface):
//   1. POST /api/devices                    — ensure a per-org "WEB-…" recorder
//      device (friendlyName below is what the confirmations feed shows as the
//      clip's source room).
//   2. POST /api/in-stream                  — upload the PCM as one ADF data
//      package on the AudioRecording (512) channel. Ingest is synchronous: the
//      chunk is in the DB + S3 before the response returns, so step 3 never
//      races it.
//   3. POST /api/devices/{serial}/label-sets/device/labels — an NxHubBatch label
//      spanning exactly the uploaded window (with x-organization-id so a
//      multi-org user's label lands in the CURRENT org, not their primary).
//   4. POST /api/nxhub/voice-messages/send  — the sendRecording.ts contract; the
//      clip then appears in Device confirmations with its "View recording" link.

import { v4 as uuidv4 } from 'uuid';
import { DataPackageBuilder } from 'web/utils/DataPackageBuilder';

/** ChannelContent.AudioRecording — the channel the NxHubBatch audio pipeline and
 * the voice-plane clip encoder read (and the one the intake web recorder writes). */
export const AUDIO_RECORDING_CHANNEL = 512;

/** Hard cap on one recorded message. Announcements are seconds long; five minutes
 * keeps the single-package upload small (~9.6 MB of 16 kHz PCM) and stays far
 * under the voice plane's ~30-minute clip cap, so a take can never be TooLong. */
export const MAX_CLIP_SECONDS = 300;

/** Floor below which a take is treated as an accidental tap, not a message. */
export const MIN_CLIP_MS = 1000;

/** The recorder device's friendly name — the confirmations feed shows the clip as
 * "Recording clip from {this}". */
export const PORTAL_RECORDER_FRIENDLY_NAME = 'Portal microphone';

/** A finished microphone take, ready to preview locally and upload on send. */
export interface RecordedTake {
    /** 16-bit mono PCM at `sampleRate`. */
    pcm: Int16Array;
    sampleRateHz: number;
    /** Wall-clock epoch ms the take started — the label/chunk window start. */
    startMs: number;
    durationMs: number;
    /** The recorder's own WAV, for the local preview player (never uploaded). */
    wavBlob: Blob;
}

/** "0:00" duration of `sampleCount` samples at `sampleRateHz`, in milliseconds. */
export function clipDurationMs(sampleCount: number, sampleRateHz: number): number {
    if (sampleRateHz <= 0) return 0;
    return Math.round((sampleCount / sampleRateHz) * 1000);
}

/**
 * Parse a 16-bit PCM WAV (what RecordRTC's StereoAudioRecorder produces) into
 * mono samples + sample rate. Walks the RIFF chunk list rather than assuming a
 * 44-byte header, and downmixes multi-channel audio by averaging. Returns null
 * for anything that is not readable 16-bit PCM — the caller maps that to a
 * user-facing "no audio" error, never a crash.
 */
export function parseWavPcm(buffer: ArrayBuffer): { sampleRateHz: number; pcm: Int16Array } | null {
    const view = new DataView(buffer);
    const ascii = (offset: number) =>
        String.fromCharCode(view.getUint8(offset), view.getUint8(offset + 1), view.getUint8(offset + 2), view.getUint8(offset + 3));

    if (buffer.byteLength < 44 || ascii(0) !== 'RIFF' || ascii(8) !== 'WAVE') return null;

    let sampleRateHz = 0;
    let channels = 0;
    let validFormat = false;
    let dataOffset = -1;
    let dataLength = 0;

    let offset = 12;
    while (offset + 8 <= buffer.byteLength) {
        const chunkId = ascii(offset);
        const chunkSize = view.getUint32(offset + 4, true);
        const body = offset + 8;
        if (chunkId === 'fmt ' && body + 16 <= buffer.byteLength) {
            const audioFormat = view.getUint16(body, true);
            channels = view.getUint16(body + 2, true);
            sampleRateHz = view.getUint32(body + 4, true);
            const bitsPerSample = view.getUint16(body + 14, true);
            validFormat = audioFormat === 1 && bitsPerSample === 16 && channels >= 1 && sampleRateHz > 0;
        } else if (chunkId === 'data') {
            dataOffset = body;
            dataLength = Math.min(chunkSize, buffer.byteLength - body);
        }
        // Chunks are word-aligned: odd sizes carry one pad byte.
        offset = body + chunkSize + (chunkSize % 2);
    }

    if (!validFormat || dataOffset < 0 || dataLength < 2) return null;

    // Copy out of the file buffer so the Int16Array is alignment-safe.
    const interleaved = new Int16Array(buffer.slice(dataOffset, dataOffset + dataLength - (dataLength % 2)));
    if (channels === 1) return { sampleRateHz, pcm: interleaved };

    const frames = Math.floor(interleaved.length / channels);
    const mono = new Int16Array(frames);
    for (let frame = 0; frame < frames; frame++) {
        let sum = 0;
        for (let ch = 0; ch < channels; ch++) sum += interleaved[frame * channels + ch];
        mono[frame] = Math.round(sum / channels);
    }
    return { sampleRateHz, pcm: mono };
}

/**
 * Build the one ADF package for POST /api/in-stream: the recorded PCM as an
 * AudioRecording (512) channel spanning [startMs, endMs], where endMs is derived
 * from the sample count so the server-computed SamplingFrequency comes out right.
 */
export function buildAdfAudioPackage(
    pcm: Int16Array,
    sampleRateHz: number,
    startMs: number,
): { fileContents: ArrayBuffer; endMs: number } {
    const endMs = startMs + clipDurationMs(pcm.length, sampleRateHz);
    const builder = new DataPackageBuilder(BigInt(startMs), BigInt(endMs), [AUDIO_RECORDING_CHANNEL]);
    builder.addSamples(pcm);
    return { fileContents: builder.build(), endMs };
}

/** What the recording shows up as in the org's recordings list. */
export const PORTAL_RECORDING_LABEL_NAME = 'Portal recording';

/** Body for the NxHubBatch label POST — exactly the uploaded chunk's window, so
 * the pipeline (and any later re-encode) loads all of it and nothing else.
 * `name` is required: labels.name is NOT NULL server-side, and AddLabelDTO
 * applies no default — omitting it 500s the whole send. */
export function recordedLabelBody(startMs: number, endMs: number): {
    type: 'NxHubBatch';
    name: string;
    fromTime: number;
    toTime: number;
    color: string;
    description: string;
} {
    return {
        type: 'NxHubBatch',
        name: PORTAL_RECORDING_LABEL_NAME,
        fromTime: startMs,
        toTime: endMs,
        color: 'PURPLE',
        description: '',
    };
}

// ── Recorder device identity ─────────────────────────────────────────────────
// One "WEB-…" recorder device per (browser, org): the org keeps the send's
// fail-closed same-org boundary honest for multi-org users, and reusing the
// serial across sessions avoids minting a device row per message.

/** The minimal slice of Storage the serial helpers need (injectable for tests). */
export interface SerialStorage {
    getItem(key: string): string | null;
    setItem(key: string, value: string): void;
}

export function recorderSerialKey(organizationId: string): string {
    return `staffAlertsRecorderSerial:${organizationId}`;
}

/**
 * The stable recorder serial for this browser + org, minting and persisting one
 * on first use. A broken/full Storage degrades to a fresh ephemeral serial —
 * recording still works, at the cost of a new device row next session.
 */
export function getRecorderSerial(organizationId: string, storage: SerialStorage): string {
    const key = recorderSerialKey(organizationId);
    try {
        const existing = storage.getItem(key);
        if (existing) return existing;
        const serial = `WEB-${uuidv4()}`;
        storage.setItem(key, serial);
        return serial;
    } catch {
        return `WEB-${uuidv4()}`;
    }
}

// ── Draft validation + send staging ──────────────────────────────────────────

export type ResolveTakeResult = { ok: true } | { ok: false; error: string };

/** Gate the Send action on a usable take. Pure — no network, no React. */
export function resolveRecordedTake(take: RecordedTake | null): ResolveTakeResult {
    if (!take || take.pcm.length === 0) {
        return { ok: false, error: 'Record a message first.' };
    }
    if (take.durationMs < MIN_CLIP_MS) {
        return { ok: false, error: 'That recording is too short to send — hold the button for at least a second.' };
    }
    // The recorder auto-stops at the cap; anything past it (clock skew, a stuck
    // timer) is refused rather than shipped to clinic speakers.
    if (take.durationMs > (MAX_CLIP_SECONDS + 2) * 1000) {
        return { ok: false, error: `Recordings are limited to ${Math.round(MAX_CLIP_SECONDS / 60)} minutes.` };
    }
    return { ok: true };
}

/** The record-mode send pipeline's user-visible stages, in order. */
export type SendStage = 'uploading' | 'saving' | 'sending';

export function sendStageLabel(stage: SendStage): string {
    switch (stage) {
        case 'uploading':
            return 'Uploading…';
        case 'saving':
            return 'Saving…';
        case 'sending':
            return 'Sending…';
    }
}
